<template>
  <section>
    <PageTitle>
      <span v-html="$store.state.app.pageName" />
    </PageTitle>
    <PageContent>
      <form @submit.prevent="checkEmail">
        <Container>
          <template v-if="editing">
            <NoticeBox type="error" v-if="hasErrors">
              {{ invalidMessage }}
            </NoticeBox>
            <Headline :level="2">会員情報</Headline>
            <div class="form-group">
              <label>お名前</label>
              <p class="text-paragraph">{{ account.name }}</p>
            </div>
            <div class="form-group">
              <label>会員番号</label>
              <p class="text-paragraph">{{ account.cmc_no }}</p>
            </div>
            <div class="form-group required">
              <label for="email">メールアドレス</label>
              <input
                id="email"
                type="text"
                class="form-control"
                :class="{ 'has-error': hasError('account.mail') }"
                v-model="account.mail"
              />
              <FormError :errors="errors['account.mail'] || []" />
            </div>
            <div class="form-group">
              <label for="tel">お電話番号</label>
              <div class="vertical-center">
                <input
                  type="text"
                  class="form-control small digits-3"
                  v-model="account.tel_area_no"
                  :class="{ 'has-error': hasError('account.tel_area_no') }"
                />
                <span class="space-x-1">-</span>
                <input
                  type="text"
                  class="form-control small digits-4"
                  v-model="account.tel_local_no"
                  :class="{ 'has-error': hasError('account.tel_local_no') }"
                />
                <span class="space-x-1">-</span>
                <input
                  type="text"
                  class="form-control small digits-4"
                  v-model="account.tel_no"
                  :class="{ 'has-error': hasError('account.tel_no') }"
                />
              </div>
              <FormError :errors="errors['account.tel_area_no'] || []" />
              <FormError :errors="errors['account.tel_local_no'] || []" />
              <FormError :errors="errors['account.tel_no'] || []" />
            </div>
            <div class="form-group">
              <label for="tel">生年月日</label>
              <YearMonthDateSelect
                v-model="account.birth_date"
                :years="birthdayYears"
              />
            </div>
            <div class="form-group" v-if="!account.divorce">
              <label for="tel">ご結婚記念日</label>
              <YearMonthDateSelect
                v-model="account.wedding_anniversary_date"
                :years="anniversaryYears"
              />
            </div>

            <div class="account_address1" v-if="!account.past_enforcement">
              <Headline :level="3">住所（ご成約時の住所）</Headline>
              <div class="form-group">
                <label>郵便番号</label>
                <div class="vertical-center">
                  <span class="space-r-half text-xs">〒</span>
                  <input
                    type="text"
                    class="form-control small digits-3"
                    maxlength="3"
                    v-model="account.address1_postcode1"
                    :class="{ 'has-error': hasError('account.address1_postcode1') }"
                  />
                  <span class="space-x-1">-</span>
                  <input
                    type="text"
                    class="form-control small digits-4 space-r-1-half"
                    maxlength="4"
                    v-model="account.address1_postcode2"
                    :class="{ 'has-error': hasError('account.address1_postcode2') }"
                  />
                  <WaveButton
                    type="button"
                    size="small"
                    class="button"
                    @click="autoFillAddress('address1')"
                  >住所入力</WaveButton>
                </div>
                <FormError :errors="errors['account.address1_postcode1'] || []" />
                <FormError :errors="errors['account.address1_postcode2'] || []" />
              </div>
              <div class="form-group">
                <label>都道府県</label>
                <div class="select">
                  <select
                    class="form-control inline"
                    v-model="account.address1_billing_state"
                  >
                    <option
                      v-for="prefecture in prefectures"
                      :value="prefecture.name"
                      :key="`prefecture-${prefecture.code}`"
                    >{{ prefecture.name }}</option>
                  </select>
                </div>
              </div>
              <div class="form-group">
                <label for="city">市区町村郡</label>
                <input
                  id="city"
                  type="text"
                  class="form-control"
                  v-model="account.address1_billing_city"
                />
              </div>
              <div class="form-group">
                <label for="town">町名番地</label>
                <input
                  id="town"
                  type="text"
                  class="form-control"
                  v-model="account.address1_billing_street"
                />
                <p class="form-memo">例）丸の内2-1-1</p>
              </div>
              <div class="form-group">
                <label for="building">建物名</label>
                <input
                  id="building"
                  type="text"
                  class="form-control"
                  v-model="account.address1_building_name"
                />
                <p class="form-memo">例）◯◯ビル 101号室</p>
              </div>
            </div>

            <h3 class="headline account_address2_confirm">住所（新居）</h3>
            <div class="form-group">
              <label class="account_address2_postcode">郵便番号</label>
              <div class="vertical-center">
                <span class="space-r-half text-xs">〒</span>
                <input
                  type="text"
                  class="form-control small digits-3"
                  maxlength="3"
                  v-model="account.address2_postcode1"
                  :class="{ 'has-error': hasError('account.address2_postcode1') }"
                />
                <span class="space-x-1">-</span>
                <input
                  type="text"
                  class="form-control small digits-4 space-r-1-half"
                  maxlength="4"
                  v-model="account.address2_postcode2"
                  :class="{ 'has-error': hasError('account.address2_postcode2') }"
                />
                <WaveButton
                  type="button"
                  size="small"
                  class="button"
                  @click="autoFillAddress('address2')"
                >住所入力</WaveButton>
              </div>
              <FormError :errors="errors['account.address2_postcode1'] || []" />
              <FormError :errors="errors['account.address2_postcode2'] || []" />
            </div>
            <div class="form-group">
              <label>都道府県</label>
              <div class="select">
                <select
                  class="form-control inline"
                  v-model="account.address2_billing_state"
                >
                  <option
                    v-for="prefecture in prefectures"
                    :value="prefecture.name"
                    :key="`prefecture-${prefecture.code}`"
                  >{{ prefecture.name }}</option>
                </select>
              </div>
            </div>
            <div class="form-group">
              <label for="city">市区町村郡</label>
              <input
                id="city"
                type="text"
                class="form-control"
                v-model="account.address2_billing_city"
              />
            </div>
            <div class="form-group">
              <label for="town">町名番地</label>
              <input
                id="town"
                type="text"
                class="form-control"
                v-model="account.address2_billing_street"
              />
              <p class="form-memo">例）丸の内2-1-1</p>
            </div>
            <div class="form-group">
              <label for="building">建物名</label>
              <input
                id="building"
                type="text"
                class="form-control"
                v-model="account.address2_building_name"
              />
              <p class="form-memo">例）◯◯ビル 101号室</p>
            </div>
            <!-- お子様情報 -->
            <ChildInfo v-if="childInfoFlag"
              :edit="editing"
              :child-info="childInfo"
              :update="updateChild"
              :child-info-flag="childInfoFlag"
              :errors="errors"
            />
            <template v-if="account.shipments.length > 0">
              <Headline :level="2">配信メール情報</Headline>
              <p class="text-light text-xxxs-m text-xs-t space-b-2">
                お受け取りになりたい情報にチェックをお願いいたします
              </p>
              <ul class="check-list">
                <li
                  class="check-list-item"
                  v-for="(shipment, sIndex) in account.shipments"
                  :key="`shipment-${sIndex}`"
                >
                  <label :for="`shipment-${sIndex}`" class="checkbox">
                    <input
                      :id="`shipment-${sIndex}`"
                      type="checkbox"
                      v-model="shipment.value"
                    />
                    <span class="checkbox-text">{{ shipment.name }}</span>
                  </label>
                </li>
              </ul>
            </template>

            <div class="form-footer">
              <div class="text-center">
                <WaveButton type="submit" class="button" :loading="loading">
                  変更内容を保存する
                </WaveButton>
              </div>
              <div class="text-center space-t-3">
                <a href="#" @click.prevent="cancel">前の画面へ戻る</a>
              </div>
            </div>
          </template>
          <template v-else>
            <NoticeBox type="info" v-if="editCompleted">
              お客様の登録情報を更新しました。
            </NoticeBox>
            <AccountConfirm :account="account" :child-info="childInfo" :child-info-flag="childInfoFlag"/>
            <div class="form-footer text-center">
              <WaveButton type="button" class="button" @click="edit">
                編集する
              </WaveButton>
            </div>
          </template>
        </Container>
      </form>
    </PageContent>
    <Modal id="check-email">
      <template #default>
        メールアドレスを変更すると、自動的にログアウトし、再ログインが必要になります。このまま変更してよろしいですか。
      </template>
      <template #footer>
        <WaveButton
          type="button"
          size="small"
          :text="true"
          @click="$modal.close('check-email')"
        >キャンセル</WaveButton>
        <WaveButton
          type="button"
          size="small"
          :text="true"
          @click="updateEmail"
        >OK</WaveButton>
      </template>
    </Modal>
  </section>
</template>

<script>
import NoticeBox from '@/components/NoticeBox.vue'
import FormError from '@/components/FormError.vue'
import AccountConfirm from '@/components/AccountConfirm.vue'
import YearMonthDateSelect from '@/components/YearMonthDateSelect.vue'
import Modal from '@/components/Modal.vue'
import validationMixin from '@/mixins/validation'
import zipApi from '@/api/zip'
import accountApi from '@/api/account'
import {
  isValidZipcode,
  getBirthdayYears,
  getAnniversaryYears } from '@/utils/form'
import { renderError, RESPONSE_EMAIL_EXISTS } from '@/utils/error'
import { isBeforeToday } from '@/utils/datetime'
import { EMAIL_EXISTS } from '@/messages/errors'
import officeApi from '@/api/office'
import ChildInfo from '@/components/ChildInfo'

export default {
  mixins: [validationMixin],
  rules: {
    'account.mail': ['メールアドレス', ['required', 'email']],
    'account.tel_area_no': ['市外局番', [
      ['pattern', '^(0|\\+)[0-9]{1,9}$', '$name$は0または+から始まる2〜10桁の数値で入力してください。']
    ]],
    'account.tel_local_no': ['市内局番', [
      ['pattern', '^[0-9]{1,10}$', '$name$は1〜10桁の数値で入力してください。']
    ]],
    'account.tel_no': ['加入者番号', [
      ['pattern', '^[0-9]{4,10}$', '$name$は4〜10桁の数値で入力してください。']
    ]],
    'account.address1_postcode1': ['郵便番号（前半）', [
      ['pattern', '^[0-9]{3}$', '$name$は3桁の数値で入力してください。']
    ]],
    'account.address1_postcode2': ['郵便番号（後半）', [
      ['pattern', '^[0-9]{4}$', '$name$は4桁の数値で入力してください。']
    ]],
    'account.address2_postcode1': ['郵便番号（前半）', [
      ['pattern', '^[0-9]{3}$', '$name$は3桁の数値で入力してください。']
    ]],
    'account.address2_postcode2': ['郵便番号（後半）', [
      ['pattern', '^[0-9]{4}$', '$name$は4桁の数値で入力してください。']
    ]],
    'account.child': ['お子様情報', [
      ['child', '', 'お子様情報は全て選択または未選択としてください。']
    ]],
  },
  components: {
    NoticeBox,
    FormError,
    AccountConfirm,
    YearMonthDateSelect,
    Modal,
    ChildInfo
  },
  metaInfo() {
    return {
      title: this.$store.state.app.pageName,
      bodyAttrs: { class: 'account' }
    }
  },
  data() {
    return {
      prefectures: [],
      birthdayYears: [],
      birthdayDays: [],
      anniversaryYears: [],
      anniversaryDays: [],
      account: {
        name: '',
        cmc_no: '',
        mail: '',
        tel_area_no: '',
        tel_local_no: '',
        tel_no: '',
        birth_date: '',
        wedding_anniversary_date: '',
        address1_postcode1: '',
        address1_postcode2: '',
        address1_billing_state: '',
        address1_billing_city: '',
        address1_billing_street: '',
        address1_building_name: '',
        address2_postcode1: '',
        address2_postcode2: '',
        address2_billing_state: '',
        address2_billing_city: '',
        address2_billing_street: '',
        address2_building_name: '',
        child1_sex: '',
        child1_year: '',
        child1_month: '',
        child2_sex: '',
        child2_year: '',
        child2_month: '',
        child3_sex: '',
        child3_year: '',
        child3_month: '',
        child4_sex: '',
        child4_year: '',
        child4_month: '',
        child5_sex: '',
        child5_year: '',
        child5_month: '',
        shipments: [],
        revision: null
      },
      oldEmail: '',
      editing: false,
      editCompleted: false,
      loading: false,
      office: {},
      childInfo: [],
      childInfoFlag: false
    }
  },
  computed: {
    userId() {
      return this.$store.getters['auth/userId']
    },
    businessId() {
      return this.$store.getters['auth/businessId']
    },
  },
  methods: {
    async autoFillAddress(addressKeyName) {
      const {
        [`${addressKeyName}_postcode1`]: z1,
        [`${addressKeyName}_postcode2`]: z2 } = this.account

      if (!isValidZipcode(z1, z2)) {
        return false
      }

      await zipApi.search(z1, z2, ({ pref, city, town, building }) => {
        this.account[`${addressKeyName}_billing_state`] = pref
        this.account[`${addressKeyName}_billing_city`] = city
        this.account[`${addressKeyName}_billing_street`] = town
        this.account[`${addressKeyName}_building_name`] = building
      })
    },
    async fetch() {
      try {
        this.account = await accountApi.show(this.userId)
        this.account.past_enforcement = isBeforeToday(
          this.account.enforce_date
        )
        this.oldEmail = this.account.mail

        for (let i = 1; i <= 5;  i++) {
          this.childInfo.push({
            sex: this.account[`child${i}_sex`],
            year: this.account[`child${i}_year`],
            month: this.account[`child${i}_month`]
          })
        }

        this.office = await officeApi.get(this.businessId)
        this.childInfoFlag = this.office.child_info_flag > 0 ? true : false
      } catch(err) {
        renderError(err)
      }
    },
    edit() {
      this.editing = true
      window.scrollTo(0, 0)
    },
    cancel() {
      if (this.loading) {
        return false
      }

      this.editing = false
      window.scrollTo(0, 0)
    },
    back() {
      this.confirming = false
    },
    checkEmail() {
      if (!this.validate()) {
        window.scrollTo(0, 0)
        return false
      }

      if (this.oldEmail === this.account.mail) {
        // メールアドレス変更なし
        return this.submit()
      }

      this.$modal.show('check-email')
    },
    updateEmail() {
      this.$modal.close('check-email')
      this.submit(() => this.$wcRouter.push({ name: 'logout' }))
    },
    async submit(onSuccess) {
      if (this.loading) {
        return false
      }

      this.loading = true

      try {
        const { revision } = await accountApi.update(this.userId, this.account)

        if (typeof onSuccess === 'function') {
          onSuccess()
          return false
        }

        this.account.revision = revision
        this.editing = false
        this.editCompleted = true
        this.loading = false
        window.scrollTo(0, 0)
      } catch(err) {
        this.loading = false

        // メールアドレスがすでに使用されていたとき
        if (err.response && err.response.status === RESPONSE_EMAIL_EXISTS) {
          this.addValidateError('account.mail', EMAIL_EXISTS)
          return window.scrollTo(0, 0)
        }

        renderError(err)
      }
    },
    initOptions() {
      this.prefectures = require('../../data/prefectures.json')
      this.birthdayYears = getBirthdayYears()
      this.anniversaryYears = getAnniversaryYears()
    },
    updateChild(info) {
      this.childInfo[info.idx].sex = info.sex
      this.childInfo[info.idx].year = info.year
      this.childInfo[info.idx].month = info.month

      this.account[`${info.key}_sex`] = this.childInfo[info.idx].sex
      this.account[`${info.key}_year`] = this.childInfo[info.idx].year
      this.account[`${info.key}_month`] = this.childInfo[info.idx].month
    }
  },
  created() {
    this.$withLoading(() => Promise.all([
      this.initOptions(),
      this.fetch()
    ]))
  }
}
</script>
